import { useMemo } from "react";
import { useSpaceDisplayName } from "../../hooks/useSpaceDisplayName";
import { formatBytes } from "../../lib/formatBytes";
import { makeUrl, routes } from "../../lib/urls";
import { useMonitoringInteraction } from "./analytics";
import { Text } from "../Text";
import { Treemap } from "../Treemap";
import { spaceColorMap } from "./monitoringPanelLayout";
import {
  foldsPersonal,
  PERSONAL_BUCKET,
  PERSONAL_BUCKET_COLOR,
} from "./monitoringScope";
import {
  buildSpaceSegments,
  OTHER_SPACES_KEY,
  type SpaceSegmentRow,
} from "./spaceSegments";
import { useMonitoringScope } from "./useMonitoringScope";
import {
  useMonitoringSpaces,
  useMonitoringWorkflows,
} from "./useMonitoringWorkflows";
import { Panel, PanelHeader, PanelSkeleton, TotalRow } from "./workflowsLayout";

export function StorageBySpace() {
  const recordInteraction = useMonitoringInteraction();
  const { report, loading } = useMonitoringWorkflows();
  const { spaces, loading: spacesLoading } = useMonitoringSpaces();
  const { scope, personalIds, matchesSpace } = useMonitoringScope(
    routes.monitoring.workflows
  );
  const displayName = useSpaceDisplayName();

  const { chartRows, totalBytes } = useMemo(() => {
    const bytesBySpace = new Map(
      report.storage.map((stat) => [stat.spaceId, stat.bytesUsed])
    );

    const chartRows: SpaceSegmentRow[] = [];
    let personalBytes = 0;
    for (const space of spaces) {
      if (!matchesSpace(space.id)) continue;
      const bytes = bytesBySpace.get(space.id) ?? 0;
      if (bytes <= 0) continue;
      if (foldsPersonal(scope) && personalIds.has(space.id)) {
        personalBytes += bytes;
        continue;
      }
      chartRows.push({ id: space.id, label: displayName(space), value: bytes });
    }
    if (personalBytes > 0) {
      chartRows.push({
        id: PERSONAL_BUCKET,
        label: "Personal",
        value: personalBytes,
      });
    }

    chartRows.sort(
      (a, b) => b.value - a.value || a.label.localeCompare(b.label)
    );

    const totalBytes = chartRows.reduce((sum, row) => sum + row.value, 0);

    return { chartRows, totalBytes };
  }, [report, spaces, scope, personalIds, matchesSpace, displayName]);

  const colorMap = useMemo(() => {
    const map = new Map(spaceColorMap(spaces));
    map.set(PERSONAL_BUCKET, PERSONAL_BUCKET_COLOR);
    return map;
  }, [spaces]);

  const segments = useMemo(
    () =>
      buildSpaceSegments(chartRows, totalBytes, colorMap, formatBytes, {
        minShare: 0.06,
        maxKept: 11,
      }),
    [chartRows, totalBytes, colorMap]
  );

  if (loading || spacesLoading) {
    return <PanelSkeleton />;
  }

  return (
    <Panel>
      <PanelHeader>
        <Text variant="text-small-md">Storage by space</Text>
        <TotalRow>
          <Text variant="display-md">{formatBytes(totalBytes)}</Text>
        </TotalRow>
      </PanelHeader>

      {chartRows.length === 0 ? (
        <Treemap segments={[]} placeholder />
      ) : (
        <Treemap
          segments={segments}
          formatValue={formatBytes}
          segmentUrl={(segment) =>
            segment.key === OTHER_SPACES_KEY || segment.key === PERSONAL_BUCKET
              ? undefined
              : makeUrl(routes.modal.storage, { spaceId: segment.key })
          }
          onSegmentNavigate={(segment) =>
            recordInteraction("workflows.storage_modal_open", {
              targetType: "space",
              targetId: segment.key,
            })
          }
        />
      )}
    </Panel>
  );
}
